/**
 * Search Plan Builder
 * Build search plan steps for different scenarios
 */

import { SEARCH_COPY, fillCopy, fillCopyWithSports } from './copy-templates.js';
import { isZipCode } from './location-comparison.js';

/**
 * Build label template for results heading
 * @param {string} city - City name or null
 * @param {string} state - State code/name or null
 * @param {string} [rawInput] - Original user input (to check if it's a ZIP code)
 * @returns {string} - Label template like "City, ST: {count} camps found" or "ZIP: {count} camps found"
 */
export function buildLabelTemplate(city, state, rawInput = null) {
  // Prefer user's raw input in heading — no automatic replacement with corrected label
  if (rawInput && String(rawInput).trim()) {
    return `${String(rawInput).trim()}: {count} camps found`;
  }
  // Normal case: use city and state (when rawInput not provided, e.g. state fallback)
  if (city && state) return `${city}, ${state}: {count} camps found`;
  if (state) return `${state}: {count} camps found`;
  return '{count} camps found';
}

/**
 * Build center object from lat/lng
 * @param {number} lat
 * @param {number} lng
 * @returns {Object|null}
 */
export function buildCenter(lat, lng) {
  if (typeof lat === 'number' && typeof lng === 'number') {
    return { lat, lng };
  }
  return null;
}

/**
 * Build state filter from state codes/names
 * @param {string} code
 * @param {string} name
 * @returns {string|null}
 */
export function buildStateFilterFromNames(code, name) {
  if (!code && !name) return null;
  const stateValues = new Set();
  if (code) stateValues.add(code);
  if (name && String(name).trim()) {
    const nameUpper = String(name).trim().toUpperCase();
    if (nameUpper !== code) stateValues.add(String(name).trim());
  }
  if (stateValues.size === 0) return null;
  const parts = Array.from(stateValues).flatMap((v) => [
    `address.state:"${v}"`,
    `address.map.state:"${v}"`,
  ]);
  return `(${parts.join(' OR ')})`;
}

/**
 * Build search plan for location-driven cases (used by Case1/3).
 * @param {Object} opts
 * @returns {Array} - Search plan steps
 */
export function buildLocationPlan(opts) {
  const {
    rawInput,
    geocode,
    hasDates,
    normalizedMismatch,
    hasSpellingMistakes,
    useDatesHeader,
    forceGlobalSubtext,
    userGeo,
  } = opts;
  const steps = [];
  const defaults = window.CampsUtils?.defaults || {};
  const radius = defaults.radius || 250;
  // For non-US users with dates, hide distance
  const isNonUS = userGeo?.isUS === false;
  const shouldHideDistance = isNonUS && hasDates;

  if (geocode?.recognized) {
    const center = buildCenter(geocode.correctedLat, geocode.correctedLng);
    const isStateInput =
      !geocode.correctedCity || geocode.correctedCity.trim() === '';
    const stateName = geocode.correctedState || geocode.correctedRegion;
    // When correction is state-level (typo like "Ilinois"->Illinois): no radius, filter by address.state. Same 103 as corrected search.
    const map = window.CampsUtils?.mapStateNameToCode;
    const userStateNorm = (userGeo?.state && (map?.(userGeo.state) || userGeo.state)) || '';
    const normalizedCodeForLoc =
      stateName ? (geocode.correctedState || map?.(geocode.correctedRegion) || geocode.correctedRegion) : '';
    const userMatchesStateForLoc =
      !!(
        normalizedCodeForLoc &&
        userStateNorm &&
        (map?.(userStateNorm) || userStateNorm) === normalizedCodeForLoc &&
        userGeo?.lat != null &&
        userGeo?.lng != null
      );
    const isStateLevelLoc = isStateInput && !!stateName;

    const stateForLabel = isStateInput
      ? geocode.correctedRegion || geocode.correctedState
      : geocode.correctedState;
    // On typo use corrected label in heading; otherwise prefer user input (no replacement)
    const rawInputForLabel =
      hasSpellingMistakes && (geocode.correctedLocationLabel || '').trim()
        ? (geocode.correctedLocationLabel || '').trim()
        : rawInput;
    const labelTemplate = buildLabelTemplate(
      geocode.correctedCity,
      stateForLabel,
      rawInputForLabel
    );
    const isZip = isZipCode(rawInput);
    const displayLocationLabel = isZip
      ? rawInput
      : geocode.correctedLocationLabel || '';
    const primaryHeader = hasSpellingMistakes
      ? fillCopy(SEARCH_COPY.unrecognizedHeader, { rawInput })
      : normalizedMismatch
      ? fillCopy(SEARCH_COPY.normalizedMismatchHeader, { rawInput })
      : null;
    const primarySub = hasSpellingMistakes
      ? fillCopyWithSports(SEARCH_COPY.unrecognizedSubtextUser, {
          locationLabel: displayLocationLabel,
        })
      : normalizedMismatch
      ? fillCopyWithSports(SEARCH_COPY.normalizedMismatchSubtext, {
          locationLabel: displayLocationLabel,
        })
      : null;

    steps.push({
      key: hasDates ? 'loc-dates' : 'loc',
      mode: 'MODE_A',
      useDistance: shouldHideDistance ? false : (isStateLevelLoc ? userMatchesStateForLoc : true),
      applyDates: hasDates,
      relaxDates: hasDates ? true : false,
      distanceCenter: isStateLevelLoc
        ? (userMatchesStateForLoc ? { lat: userGeo.lat, lng: userGeo.lng } : null)
        : center,
      // For recognized locations, always apply radius (standard behavior)
      // For state-level locations, no radius (filter by address.state)
      radiusMiles: isStateLevelLoc ? null : radius,
      stateFilter: null,
      locationLabel: geocode.correctedLocationLabel || '',
      labelTemplate,
      headerHtml: primaryHeader,
      subtextHtml: primarySub,
    });

    // State fallback
    console.log(`📍 [BUILD LOCATION PLAN] Checking state fallback:`, {
      correctedState: geocode.correctedState,
      correctedRegion: geocode.correctedRegion,
      stateName,
      hasStateName: !!stateName,
      hasDates,
    });
    if (stateName) {
      const fullStateName = geocode.correctedRegion || geocode.correctedState;
      const map = window.CampsUtils?.mapStateNameToCode;
      const normalizedCode =
        geocode.correctedState || map?.(geocode.correctedRegion) || geocode.correctedRegion;
      const stateFilterStr = buildStateFilterFromNames(normalizedCode, fullStateName);

      // State step: filter by address.state (no geo radius). Show distance only when user's state matches.
      const userStateNorm = (userGeo?.state && (map?.(userGeo.state) || userGeo.state)) || '';
      const userMatchesState =
        normalizedCode &&
        userStateNorm &&
        (map?.(userStateNorm) || userStateNorm) === normalizedCode &&
        userGeo?.lat != null &&
        userGeo?.lng != null;

      const displayLocationLabelForState = isZip
        ? rawInput
        : geocode.correctedLocationLabel || '';

      console.log(
        `📍 [BUILD LOCATION PLAN] Adding state fallback step: ${fullStateName}, userMatchesState=${userMatchesState}`
      );

      steps.push({
        key: hasDates ? 'state-dates' : 'state',
        mode: 'MODE_A',
        useDistance: shouldHideDistance ? false : userMatchesState,
        applyDates: hasDates,
        relaxDates: hasDates ? true : false,
        distanceCenter: userMatchesState ? { lat: userGeo.lat, lng: userGeo.lng } : null,
        radiusMiles: null,
        stateFilter: stateFilterStr,
        locationLabel: fullStateName,
        labelTemplate: buildLabelTemplate(null, fullStateName),
        headerHtml: isStateInput
          ? null
          : useDatesHeader
          ? fillCopyWithSports(SEARCH_COPY.datesLocationZeroHeader, {
              locationLabel: displayLocationLabelForState,
            })
          : fillCopyWithSports(SEARCH_COPY.normalizedZeroHeader, {
              locationLabel: displayLocationLabelForState,
            }),
        subtextHtml: isStateInput
          ? null
          : useDatesHeader
          ? fillCopyWithSports(SEARCH_COPY.datesLocationStateSubtext, {
              stateCode: fullStateName,
            })
          : fillCopyWithSports(SEARCH_COPY.normalizedZeroSubtext, {
              stateCode: fullStateName,
            }),
      });
    }

    // Add global fallback for case without dates: after state fallback, check global search
    // This ensures we check state level first, then expand to global
    if (!hasDates) {
      // Set center based on user location or fallback for sorting in Algolia
      const globalCenter =
        buildCenter(userGeo?.lat, userGeo?.lng) ||
        buildCenter(
          defaults.fallbackCoords?.lat,
          defaults.fallbackCoords?.lng
        );
      steps.push({
        key: 'loc-global',
        mode: 'MODE_D',
        useDistance: false,
        applyDates: false,
        relaxDates: false,
        distanceCenter: globalCenter, // Use user location or fallback for Algolia sorting
        radiusMiles: null,
        stateFilter: null,
        locationLabel: null,
        labelTemplate: buildLabelTemplate(null, null),
        headerHtml: fillCopyWithSports(SEARCH_COPY.normalizedZeroHeader, {
          locationLabel: displayLocationLabel,
        }),
        subtextHtml: fillCopy(SEARCH_COPY.unrecognizedSubtextGlobal, {}),
      });
    }
  } else {
    // Unrecognized location: don't use city/state from userGeo (they're from previous geocode, not current search)
    // Only use coordinates for sorting (IP-based for US, San Rafael for non-US)
    const userGeo = opts.userGeo || null;
    const isNonUS = userGeo?.isUS === false;
    // Use coordinates for sorting: San Rafael for non-US, IP coords for US (but don't show city/state in text)
    const center = isNonUS
      ? buildCenter(
          defaults.fallbackCoords?.lat,
          defaults.fallbackCoords?.lng
        )
      : buildCenter(userGeo?.lat, userGeo?.lng);
    const hasUserCenter = !!center;

    steps.push({
      key: isNonUS ? 'global-nonus' : hasUserCenter ? 'user-geo' : 'global',
      mode: isNonUS ? 'MODE_B' : hasUserCenter ? 'MODE_A' : 'MODE_D',
      useDistance: isNonUS ? false : hasUserCenter,
      applyDates: hasDates,
      relaxDates: false,
      distanceCenter: center,
      radiusMiles: null, // NO radius for unrecognized location - show ALL camps sorted by distance
      stateFilter: null,
      // Never use city/state from userGeo for unrecognized - they're from previous search, not current
      locationLabel: null,
      labelTemplate: buildLabelTemplate(null, null),
      headerHtml: fillCopy(SEARCH_COPY.unrecognizedHeader, { rawInput }),
      subtextHtml: fillCopy(SEARCH_COPY.unrecognizedSubtextGlobal, {}),
    });
  }

  if (forceGlobalSubtext) {
    steps[steps.length - 1].subtextHtml = fillCopy(
      SEARCH_COPY.unrecognizedSubtextGlobal,
      {}
    );
    steps[steps.length - 1].mode = 'MODE_D';
    steps[steps.length - 1].useDistance = false;
    steps[steps.length - 1].distanceCenter = null;
    steps[steps.length - 1].radiusMiles = null;
    steps[steps.length - 1].labelTemplate = buildLabelTemplate(null, null);
  }

  return steps;
}

console.log('📦 Plan builder module loaded');
